import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/common/SEO';
import HeroBanner from '../components/home/HeroBanner';
import BookSection from '../components/shop/BookSection';
import StoreSection from '../components/shop/StoreSection';
import PromoSlider from '../components/home/PromoSlider';
import MiscShowcase from '../components/home/MiscShowcase';
import OffersSection from '../components/home/OffersSection';
import RecentlyViewed from '../components/shop/RecentlyViewed';
import { SkeletonBookGrid } from '../components/common/Skeleton';
import { apiGetHomeBooks, apiGetHomeSectionVisibility, apiGetSiteSection } from '../api';
import logger from '../utils/logger';

const RESERVED_HOME_CATEGORY_SLUGS = new Set(['new-releases', 'bestsellers']);
const SHOWCASE_CATEGORY_PRIORITY = ['travel', 'new-releases', 'religious', 'gift-a-book'];
const isOfferCategory = (category) =>
  /^offers?(?:-|$)/.test(category?.slug?.trim().toLowerCase() || '') ||
  /^offers?(?:\s|$)/i.test(category?.name?.trim() || '');

const getBookCategory = (book) => {
  if (!book.category) return null;
  if (typeof book.category === 'string') {
    return { id: null, name: book.category, slug: null };
  }
  return {
    id: book.category.id ?? book.categoryId ?? null,
    name: book.category.name || '',
    slug: book.category.slug || null,
  };
};

const getCategoryLink = (category) =>
  `/shop?category=${encodeURIComponent(category).replace(/%20/g, '+')}`;

const groupBooksByCategory = (books, limit) => {
  const booksByCategory = new Map();
  books.forEach((book) => {
    const category = getBookCategory(book);
    if (!category?.name) return;

    const key = category.id ? `id:${category.id}` : `name:${category.name.trim().toLowerCase()}`;
    const group = booksByCategory.get(key) || { category, books: [] };
    if (group.books.length < limit) group.books.push(book);
    booksByCategory.set(key, group);
  });
  return booksByCategory;
};

const showcasePriority = ({ category }) => {
  const slug = category.slug?.trim().toLowerCase();
  const nameSlug = category.name.trim().toLowerCase().replace(/\s+/g, '-');
  const priority = SHOWCASE_CATEGORY_PRIORITY.findIndex((key) => key === slug || key === nameSlug);
  return priority < 0 ? SHOWCASE_CATEGORY_PRIORITY.length : priority;
};

const buildShowcaseCards = (books) =>
  Array.from(groupBooksByCategory(books, 4).values())
    .filter(({ category }) => !isOfferCategory(category))
    .sort((first, second) => showcasePriority(first) - showcasePriority(second))
    .slice(0, 4)
    .map(({ category, books: items }) => ({
      category: category.name,
      title: `Explore ${category.name}`,
      items,
      link: getCategoryLink(category.name),
    }));

const Home = () => {
  const location = useLocation();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionVisibility, setSectionVisibility] = useState({});
  const [homeSettings, setHomeSettings] = useState({});
  const isVisible = (key) => sectionVisibility[key] !== false;

  useEffect(() => {
    apiGetHomeBooks()
      .then((data) => setBooks(data.books || []))
      .catch((e) => logger.error('Failed to fetch books', e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    apiGetSiteSection('home_settings')
      .then(({ data }) => setHomeSettings(data?.[0] || {}))
      .catch((error) => logger.error('Failed to fetch home settings', error));
  }, []);

  useEffect(() => {
    apiGetHomeSectionVisibility()
      .then(({ data }) => setSectionVisibility(data || {}))
      .catch((error) => logger.error('Failed to fetch home section visibility', error));
  }, []);

  // Product sections render after the API request, so wait for loading to
  // finish before scrolling to a section linked from the category drawer.
  useEffect(() => {
    if (loading || !location.hash) return;

    const section = document.getElementById(location.hash.slice(1));
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const bestsellerBooks = books.filter((book) => book.isBestseller).slice(0, 8);
  const newReleaseBooks = [...books]
    .sort((first, second) => {
      const firstDate = Date.parse(first.createdAt || first.updatedAt || 0) || 0;
      const secondDate = Date.parse(second.createdAt || second.updatedAt || 0) || 0;
      return secondDate - firstDate;
    })
    .slice(0, 8);
  const categorySections = Array.from(groupBooksByCategory(books, 25).values()).filter(
    ({ category }) =>
      !RESERVED_HOME_CATEGORY_SLUGS.has(category.slug?.trim().toLowerCase()) &&
      !isOfferCategory(category)
  );
  const offerSection = Array.from(groupBooksByCategory(books, Number.POSITIVE_INFINITY).values()).find(
    ({ category }) => isOfferCategory(category)
  );
  const showcaseCards = buildShowcaseCards(books);

  return (
    <>
      <SEO
        title={homeSettings.seoTitle || 'Home'}
        description={homeSettings.seoDescription || 'Discover products online.'}
        keywords={homeSettings.seoKeywords || ''}
      />
      {isVisible('hero') && <HeroBanner />}
      {loading && (
        <section className="w-full mx-auto px-4 py-3">
          <SkeletonBookGrid count={10} />
        </section>
      )}
      {!loading && isVisible('showcase') && showcaseCards.length > 0 && <MiscShowcase cards={showcaseCards} />}
      {!loading && isVisible('bestsellers') && bestsellerBooks.length > 0 && (
        <BookSection id="bestsellers" title={homeSettings.bestsellersTitle || 'Bestsellers'} books={bestsellerBooks} />
      )}
      {!loading && isVisible('offers') && offerSection && (
        <OffersSection category={offerSection.category} books={offerSection.books} />
      )}
      {!loading && isVisible('recently_viewed') && <RecentlyViewed />}
      {!loading && isVisible('new_releases') && newReleaseBooks.length > 0 && (
        <BookSection id="new-releases" title={homeSettings.newReleasesTitle || 'New Releases'} books={newReleaseBooks} />
      )}
      {!loading && isVisible('categories') && categorySections.map(({ category, books: items }) => {
        if (category.id && !isVisible(`category:${category.id}`)) return null;
        return (
          <BookSection
            key={category.id || category.slug || category.name}
            title={category.name}
            books={items}
          />
        );
      })}
      {isVisible('stores') && <StoreSection />}
      {isVisible('promotions') && <PromoSlider />}
    </>
  );
};

export default Home;
